import { useTranslation } from 'react-i18next';

import type { WebsiteStepProps } from '../types';
import type { WebsiteDraft } from '../types';
import { WEBSITE_TEMPLATES, type WebsiteTemplate } from '../../api/websites';

// TemplatePicker is step 1. Four cards with hand-drawn-style SVG
// wireframes that hint at the structural differences between templates
// (nav placement, hero style, section ordering). The pick is the only
// thing this step writes to the draft.

const SVG_BY_TEMPLATE: Record<WebsiteTemplate, JSX.Element> = {
  classic: (
    <svg viewBox="0 0 120 80" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="112" height="6" rx="1" />
      <rect x="4" y="14" width="60" height="18" rx="1" />
      <rect x="68" y="14" width="48" height="18" rx="1" />
      <rect x="4" y="36" width="35" height="14" rx="1" />
      <rect x="43" y="36" width="35" height="14" rx="1" />
      <rect x="81" y="36" width="35" height="14" rx="1" />
      <rect x="4" y="54" width="112" height="14" rx="1" />
      <rect x="4" y="72" width="112" height="4" rx="1" />
    </svg>
  ),
  cover: (
    <svg viewBox="0 0 120 80" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="112" height="40" rx="1" />
      <rect x="10" y="10" width="50" height="3" rx="1" />
      <rect x="60" y="10" width="50" height="3" rx="1" />
      <rect x="4" y="48" width="60" height="22" rx="1" />
      <rect x="68" y="48" width="48" height="22" rx="1" />
      <rect x="4" y="74" width="112" height="2" rx="1" />
    </svg>
  ),
  magazine: (
    <svg viewBox="0 0 120 80" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="112" height="4" rx="1" />
      <rect x="4" y="12" width="112" height="6" rx="1" />
      <rect x="4" y="22" width="74" height="22" rx="1" />
      <rect x="82" y="22" width="34" height="10" rx="1" />
      <rect x="82" y="34" width="34" height="10" rx="1" />
      <rect x="4" y="48" width="35" height="22" rx="1" />
      <rect x="43" y="48" width="35" height="22" rx="1" />
      <rect x="82" y="48" width="34" height="22" rx="1" />
      <rect x="4" y="74" width="112" height="2" rx="1" />
    </svg>
  ),
  siderail: (
    <svg viewBox="0 0 120 80" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="22" height="72" rx="1" />
      <rect x="30" y="4" width="86" height="20" rx="1" />
      <rect x="30" y="28" width="86" height="14" rx="1" />
      <rect x="30" y="44" width="86" height="14" rx="1" />
      <rect x="30" y="60" width="86" height="14" rx="1" />
    </svg>
  ),
};

// slugify normalises the user's typed value into the wire format the
// backend's slug constraint accepts: lowercase alphanumerics with
// single-dash separators, no leading/trailing dashes, no consecutive
// dashes. Run on every keystroke as the user types in the slug field
// so they see exactly what will hit the server.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
    .replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function TemplatePicker({ draft, update }: WebsiteStepProps) {
  const { t } = useTranslation('common');

  // slugTouched tracks whether the user has hand-edited the slug. Until
  // they do, typing in the name field auto-syncs the slug field to a
  // slugified copy of the name — same UX shape as the team-create flow.
  // Once the user edits the slug, we stop auto-syncing so a deliberate
  // value sticks.
  const slugTouched = draft.slug !== '' && draft.slug !== slugify(draft.name);

  const handleNameChange = (next: string) => {
    if (slugTouched) {
      update({ name: next });
    } else {
      update({ name: next, slug: slugify(next) });
    }
  };

  return (
    <div className="wizard-templates">
      <div className="wizard-templates__name">
        <label htmlFor="wizard-website-name">
          {t('website.wizard.name.label')}
        </label>
        <input
          id="wizard-website-name"
          type="text"
          value={draft.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder={t('website.wizard.name.placeholder')}
          maxLength={120}
          autoComplete="off"
        />
        <p className="wizard-templates__name-hint">
          {t('website.wizard.name.hint')}
        </p>
      </div>
      <div className="wizard-templates__name">
        <label htmlFor="wizard-website-slug">
          {t('website.wizard.slug.label')}
        </label>
        <input
          id="wizard-website-slug"
          type="text"
          value={draft.slug}
          onChange={(e) => update({ slug: slugify(e.target.value) })}
          placeholder={t('website.wizard.slug.placeholder')}
          maxLength={80}
          autoComplete="off"
          inputMode="text"
          pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
        />
        <p className="wizard-templates__name-hint">
          {t('website.wizard.slug.hint', { slug: draft.slug || '…' })}
        </p>
      </div>
      <div className="wizard-templates__grid">
        {WEBSITE_TEMPLATES.map((tpl) => {
          const selected = draft.template === tpl;
          return (
            <button
              key={tpl}
              type="button"
              className={'wizard-template-card' + (selected ? ' wizard-template-card--selected' : '')}
              aria-pressed={selected}
              onClick={() => update({ template: tpl })}
            >
              <div className="wizard-template-card__svg">{SVG_BY_TEMPLATE[tpl]}</div>
              <div className="wizard-template-card__body">
                <h3>{t(`website.wizard.template.${tpl}.name`)}</h3>
                <p>{t(`website.wizard.template.${tpl}.description`)}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function templatePickerIsValid(d: WebsiteDraft): boolean {
  // Slug must be present and match the same lowercase-dash format the
  // server enforces — keep the SPA-side validator in lockstep with
  // internal/services/websites/service.go's slugRegexp.
  const slugOK = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(d.slug) && d.slug.length <= 80;
  return !!d.template && d.name.trim().length > 0 && slugOK;
}
